import { FerveCartItem } from "@features/ferve/ferve-cart"
import { BrowseFilmTileFragment, FilmSessionFragment } from "@generated-types"
import { useProgramFilms } from "@hooks/queries"
import { formatFerveDate } from "@utils/date-format"
import dayjs from "dayjs"
import { useMemo } from "react"

type SessionResolverFunction = (session: FilmSessionFragment, film: BrowseFilmTileFragment) => boolean
type SessionResolver = string | number | SessionResolverFunction

type ResolvedSession = {
  session: FilmSessionFragment
  film: BrowseFilmTileFragment
  date: dayjs.Dayjs
}

export function useProgramData(args: { enabled?: boolean } = {}) {
  return useProgramFilms(undefined, {
    staleTime: 60 * 1000 * 10,
    networkMode: "always",
    structuralSharing: true,
    enabled: args.enabled !== false,
  })
}

export async function fetchProgramData() {
  return useProgramFilms.fetch(undefined, {
    staleTime: 60 * 1000 * 10,
    networkMode: "always",
    structuralSharing: true,
  })
}

export function useFindSession(match: SessionResolver, deps: any[] = []) {
  const lookup = useProgramData()

  return useMemo(() => {
    return resolveSession(lookup.data?.currentProgram?.films ?? [], match)
  }, [lookup.data?.currentProgram?.films, ...deps])
}

export async function findProgramSession(match: SessionResolver, deps: any[] = []) {
  const lookup = await fetchProgramData()

  return resolveSession(lookup.currentProgram?.films ?? [], match)
}

export function resolveFerveCartSession(
  films: (BrowseFilmTileFragment | null)[],
  item: FerveCartItem,
): ResolvedSession | null {
  const ferveDate = item.dateTime && formatFerveDate(item.dateTime)

  const matcher: SessionResolverFunction = (session, film) => {
    return item.title === film.title && session.info?.dateTime === ferveDate
  }
  return resolveSession(films, matcher)
}

export function resolveSession(
  films: (BrowseFilmTileFragment | null)[],
  match: SessionResolver,
): ResolvedSession | null {
  const matcher: SessionResolverFunction =
    typeof match === "function"
      ? match
      : (session: FilmSessionFragment, film: BrowseFilmTileFragment) => {
          return String(session?.info?.ferveId) === match || session?.info?.hash === match
        }
  for (const film of films ?? []) {
    for (const session of film?.sessions ?? []) {
      if (matcher(session!, film!)) {
        return {
          session: session!,
          film: film!,
          date: dayjs(session?.info?.dateTime),
        }
      }
    }
  }
  return null
}

export function resolveSessions(
  films: (BrowseFilmTileFragment | null)[],
  matcher: SessionResolverFunction,
): ResolvedSession[] {
  const results: ResolvedSession[] = []
  for (const film of films ?? []) {
    for (const session of film?.sessions ?? []) {
      if (matcher(session!, film!)) {
        results.push({
          session: session!,
          film: film!,
          date: dayjs(session?.info?.dateTime),
        })
      }
    }
  }
  return results.sort((a, b) => a.date.diff(b.date)) ?? []
}
